import { useApiSegmentsRange } from '@app/core/hooks/api/useApiSegmentsRange';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { getRangeForSegments } from '../../helpers/get-range-for-segments';
import { useStreamUserDefinedRange } from '../../stores/stream-screen.store';
import { useIsStreamRangeInFuture } from '../range/useIsStreamRangeInFuture';
import { useIsStreamRangeTooRecent } from '../range/useIsStreamRangeTooRecent';

/**
 * Hook to fetch segments for the current stream range.
 * Uses React Query for caching and automatic refetching.
 * Does not fetch when the range is entirely in the future or when the range
 * start is within the minimum lookback window (e.g. last 20 minutes).
 */
export function useStreamSegmentsQuery() {
  const station = useActiveStation();
  const { from, to, isLoading } = useStreamUserDefinedRange();
  const isFutureRange = useIsStreamRangeInFuture();
  const isTooRecent = useIsStreamRangeTooRecent();

  // Only fetch if range is initialized, not in the future, and not too recent
  const enabled = !isLoading && !isFutureRange && !isTooRecent;

  return useApiSegmentsRange(station.id, from, to, {
    enabled,
  });
}

/**
 * Hook to check if segments are loading.
 * Used by StreamContent to show a loader while segments are being fetched.
 */
/**
 * When the query is disabled (future or too recent range), TanStack Query never runs it,
 * so it stays status "pending" with no data and isPending remains true. Use isLoading
 * (true only when actually fetching) so we don't show the loader when we're not fetching.
 */
export function useStreamSegments() {
  const query = useStreamSegmentsQuery();

  return {
    isLoading: query.isLoading,
    segments: query.data ?? [],
  };
}

/**
 * Get segments list from query data.
 */
export function useStreamSegmentsList() {
  return useStreamSegments().segments;
}

/**
 * Get segments range (from first segment start to last segment end).
 * This is the real range of the segments, not the user defined range.
 */
export function useStreamSegmentsRange() {
  const segments = useStreamSegmentsList();

  return getRangeForSegments(segments);
}

/**
 * Get segments range (from first segment start to last segment end).
 * This is the real range of the segments, not the user defined range.
 */
export function useStreamSegmentsRange_v2() {
  const { segments, isLoading } = useStreamSegments();

  return {
    isLoading,
    range: getRangeForSegments(segments),
  };
}
